// import: styles
import { Avatar, Box } from '@mui/material';
import upgradeImage from 'core/assets/img/home-page/profile-button-home.webp';
import Coin from 'core/assets/img/pricing-page/coin.webp';
import { ReactComponent as DefaultPfp } from 'core/assets/svg/app-bar/popover/default_pfp.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import Linked from 'core/components/link/Link';

// import: components
import BaseTypography from 'core/components/typography/BaseTypography';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { profileButtonStyles } from 'core/layouts/components/profile-button/ProfileButtonStyles';
import { profileCardStyles } from 'core/layouts/components/profile-card/ProfileCardStyles';
import UpgradeTo from 'core/layouts/components/upgrade-to/UpgradeTo';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
// import: constants
import { PRICING } from 'core/navigation/Routes';

import {
	getUserProfileAPIStageSelector,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

function ProfileCard() {
	const userProfile = useSelector(userProfileSelector);
	const getProfileAPIStage = useSelector(getUserProfileAPIStageSelector);

	const isLoading = useMemo(() => getProfileAPIStage === ApiStage.LOADING, [getProfileAPIStage]);

	return (
		<Box id="drawer-profile-section" sx={[profileCardStyles.profileSection]}>
			<Box sx={[reusableStyles.flexColWithGap(4), { alignItems: 'start' }]}>
				<Box
					id="pop-over-avatar"
					sx={[profileButtonStyles.avatarCont, { height: '48px', width: '48px' }]}
				>
					<Avatar src={userProfile.profileUrl} alt="Profile avatar" sx={profileButtonStyles.avatar}>
						<DefaultPfp />
					</Avatar>
				</Box>
				<Box sx={[reusableStyles.flexCol, { width: '100%', alignItems: 'start' }]}>
					{isLoading ? (
						<React.Fragment key="pop-over-avatar-loading-user-detail">
							<BaseTypography
								variant="body1"
								styles={[
									{ width: '80px', marginBottom: '4px' },
									reusableStyles.skeletonLoaderStyles,
								]}
							/>
							<Box
								sx={[
									reusableStyles.flexRow,
									{ justifyContent: 'space-between', width: '100%', alignItems: 'center' },
								]}
							>
								<BaseTypography
									sx={[reusableStyles.skeletonLoaderStyles, { width: '50%', marginBottom: '8px' }]}
									variant="b1_regular"
									color="colors.light4"
								/>
								<Box sx={[reusableStyles.skeletonLoaderStyles, reusableStyles.flexRow]}>
									<BaseTypography
										variant="b1_medium"
										styles={{ color: 'colors.light3', width: '40px' }}
									/>
								</Box>
							</Box>
						</React.Fragment>
					) : (
						<React.Fragment key="pop-over-avatar-user-detail">
							<BaseTypography variant="b1_medium" color="colors.light2">
								{userProfile.name}
							</BaseTypography>
							<Box
								sx={[reusableStyles.flexRow, { justifyContent: 'space-between', width: '100%' }]}
							>
								<BaseTypography variant="b1_regular" color="colors.light4">
									@{userProfile.handle?.substring(0, 10)}
									{userProfile.handle?.length > 10 && '...'}
								</BaseTypography>
								<Box sx={[reusableStyles.flexRow, { alignItems: 'center' }]}>
									<BaseImage src={Coin} alt="coin" style={{ height: '12px', width: 'auto' }} />
									<BaseTypography variant="b1_medium" styles={{ color: 'colors.light3' }}>
										{userProfile?.credits?.balance}
									</BaseTypography>
								</Box>
							</Box>
						</React.Fragment>
					)}
				</Box>
			</Box>
			{!window.electron &&
				(isLoading ? (
					<Box>
						<BaseButton
							id="profile-popover-buy-credits-button"
							sx={[
								reusableStyles.skeletonLoaderStyles,
								{
									minWidth: 'unset',
									width: '100%',
									textTransform: 'none',
									padding: '14px',
									borderRadius: '15px',
								},
							]}
							disableRipple
						/>
					</Box>
				) : (
					<Box
						sx={{
							background: `url(${upgradeImage})`,
							backgroundSize: 'contain',
							backgroundRepeat: 'no-repeat',
						}}
					>
						<Linked to={`/${PRICING}`} target="_blank">
							<BaseButton
								id="profile-popover-buy-credits-button"
								sx={{
									minWidth: 'unset',
									padding: 0,
									width: '100%',
									textTransform: 'none',
								}}
								disableRipple
							>
								<UpgradeTo userProfile={userProfile} isProfileDataLoading={isLoading} />
							</BaseButton>
						</Linked>
					</Box>
				))}
		</Box>
	);
}

export default ProfileCard;
